import { motion } from 'motion/react';
import { RegistroCta } from '@/features/auth/components/registro-cta';
import {
  ArrowRight,
  CheckCircle,
  FileText,
  BarChart3,
  ShieldCheck,
  Clock,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';
import { ScrollReveal } from '@/components/motion/scroll-reveal';
import { MockupWrapper, ShowcaseBg } from './showcase-utils';

// ── Quote builder mockup ─────────────────────────────────────────

function QuoteMockup() {
  const lineItems = [
    { name: 'Producto A — Serie 400', qty: 120, unit: '$42.00', total: '$5,040' },
    { name: 'Instalación estándar', qty: 1, unit: '$1,200', total: '$1,200' },
    { name: 'Garantía extendida 2yr', qty: 1, unit: '$380', total: '$380' },
  ];

  return (
    <>
      {/* Quote header */}
      <div
        className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4"
        style={{ transform: 'translateZ(20px)' }}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Cliente</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              Corporación ABC S.A.
            </p>
          </div>
          <span className="text-[10px] px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 font-medium border border-blue-100 dark:border-blue-500/20">
            Borrador
          </span>
        </div>
        <div className="flex gap-4">
          <div>
            <p className="text-[10px] text-gray-400 dark:text-gray-500">Modelo</p>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
              Industrial B2B v3
            </p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 dark:text-gray-500">Validez</p>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">30 días</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 dark:text-gray-500">Moneda</p>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">USD</p>
          </div>
        </div>
      </div>

      {/* Line items */}
      <div
        className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden"
        style={{ transform: 'translateZ(12px)' }}
      >
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 px-4 py-2 bg-gray-50 dark:bg-gray-800/80 border-b border-gray-100 dark:border-gray-700">
          {['Ítem', 'Cant.', 'Precio', 'Total'].map((h) => (
            <p
              key={h}
              className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase"
            >
              {h}
            </p>
          ))}
        </div>
        {lineItems.map((item, i) => (
          <div
            key={i}
            className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 px-4 py-2.5 border-b border-gray-50 dark:border-gray-700/50 last:border-0"
          >
            <p className="text-[11px] text-gray-700 dark:text-gray-300 truncate">{item.name}</p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 text-right">{item.qty}</p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 text-right">{item.unit}</p>
            <p className="text-[11px] font-semibold text-gray-800 dark:text-gray-200 text-right">
              {item.total}
            </p>
          </div>
        ))}
      </div>

      {/* Totals */}
      <div
        className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4"
        style={{ transform: 'translateZ(16px)' }}
      >
        <div className="space-y-1.5">
          {[
            { label: 'Subtotal', value: '$6,620', muted: true },
            { label: 'Descuento (5%)', value: '−$331', muted: true },
            { label: 'IVA (13%)', value: '$813', muted: true },
          ].map(({ label, value }) => (
            <div key={label} className="flex justify-between">
              <span className="text-[11px] text-gray-400 dark:text-gray-500">{label}</span>
              <span className="text-[11px] text-gray-600 dark:text-gray-400">{value}</span>
            </div>
          ))}
          <div className="flex justify-between pt-2 border-t border-gray-100 dark:border-gray-700 mt-2">
            <span className="text-xs font-bold text-gray-900 dark:text-white">Total</span>
            <span className="text-sm font-bold text-blue-600 dark:text-blue-400">$7,102</span>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Approvals mockup ─────────────────────────────────────────────

function ApprovalsMockup() {
  const items = [
    {
      id: 'Q-2847',
      client: 'Importaciones XYZ',
      amount: '$128,500',
      discount: '18%',
      status: 'pending',
      urgent: true,
    },
    {
      id: 'Q-2841',
      client: 'Tech Solutions Ltda.',
      amount: '$45,200',
      discount: '8%',
      status: 'approved',
      urgent: false,
    },
    {
      id: 'Q-2835',
      client: 'Distribuidora Norte',
      amount: '$67,800',
      discount: '22%',
      status: 'review',
      urgent: false,
    },
  ];

  const statusConfig: Record<string, { label: string; color: string }> = {
    pending: {
      label: 'Pendiente',
      color:
        'text-yellow-600 bg-yellow-50 border-yellow-100 dark:text-yellow-400 dark:bg-yellow-500/10 dark:border-yellow-500/20',
    },
    approved: {
      label: 'Aprobado',
      color:
        'text-green-600 bg-green-50 border-green-100 dark:text-green-400 dark:bg-green-500/10 dark:border-green-500/20',
    },
    review: {
      label: 'En revisión',
      color:
        'text-blue-600 bg-blue-50 border-blue-100 dark:text-blue-400 dark:bg-blue-500/10 dark:border-blue-500/20',
    },
  };

  return (
    <>
      {/* Alert banner */}
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.5 }}
        className="bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-200 dark:border-yellow-500/20 rounded-xl px-4 py-3 flex items-center gap-3"
        style={{ transform: 'translateZ(20px)' }}
      >
        <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
        <p className="text-xs text-yellow-700 dark:text-yellow-300">
          <span className="font-semibold">3 cotizaciones</span> requieren tu aprobación — descuentos
          fuera de rango
        </p>
      </motion.div>

      {/* Approval list */}
      <div
        className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden"
        style={{ transform: 'translateZ(12px)' }}
      >
        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 dark:border-gray-700/50 last:border-0 hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors"
          >
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-violet-400 flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0">
              {item.client[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-[11px] font-semibold text-gray-800 dark:text-gray-200">
                  {item.id}
                </p>
                {item.urgent && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-500/20 font-medium">
                    Urgente
                  </span>
                )}
              </div>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate">{item.client}</p>
            </div>
            <div className="text-right mr-2">
              <p className="text-xs font-bold text-gray-900 dark:text-white">{item.amount}</p>
              <p className="text-[10px] text-orange-500 dark:text-orange-400">
                Dto. {item.discount}
              </p>
            </div>
            <span
              className={`text-[9px] px-2 py-1 rounded-full border font-medium ${statusConfig[item.status].color}`}
            >
              {statusConfig[item.status].label}
            </span>
          </div>
        ))}
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-3" style={{ transform: 'translateZ(16px)' }}>
        {[
          {
            icon: Clock,
            label: 'Tiempo prom.',
            value: '4.2h',
            color: 'text-blue-600 dark:text-blue-400',
          },
          {
            icon: CheckCircle,
            label: 'Aprobadas hoy',
            value: '12',
            color: 'text-green-600 dark:text-green-400',
          },
          {
            icon: ShieldCheck,
            label: 'Auto-aprobadas',
            value: '68%',
            color: 'text-violet-600 dark:text-violet-400',
          },
        ].map(({ icon: Icon, label, value, color }) => (
          <div
            key={label}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-3 text-center"
          >
            <Icon className={`w-4 h-4 mx-auto mb-1 ${color}`} />
            <p className="text-base font-bold text-gray-900 dark:text-white">{value}</p>
            <p className="text-[9px] text-gray-400 dark:text-gray-500">{label}</p>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Main export ──────────────────────────────────────────────────

export function ProductShowcaseSection() {
  return (
    <>
      {/* ── Block 1: Quote builder — text left, mockup right ── */}
      <section id="product-showcase" className="relative py-24 sm:py-32 overflow-hidden">
        <ShowcaseBg />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Text */}
            <ScrollReveal direction="left" className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/10 shadow-sm">
                <FileText className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                  Cotizaciones
                </span>
              </div>

              <div className="space-y-2">
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white leading-tight">
                  Tu equipo cotiza en minutos,
                  <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600">
                    no en horas.
                  </span>
                </h2>
                <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                  Olvídate de copiar y pegar en Excel. Define tus modelos de pricing una vez y el
                  sistema calcula márgenes, descuentos e impuestos automáticamente.
                </p>
              </div>

              <ul className="space-y-3">
                {[
                  'Modelos de precios con fórmulas personalizables',
                  'Múltiples secciones por cotización, cada una con su modelo',
                  'Materiales vinculados con precios actualizados',
                  'PDF profesional generado en un clic',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">{item}</span>
                  </li>
                ))}
              </ul>

              <RegistroCta className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors group">
                Crear mi primera cotización
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </RegistroCta>
            </ScrollReveal>

            {/* Mockup */}
            <MockupWrapper>
              <QuoteMockup />
            </MockupWrapper>
          </div>
        </div>
      </section>

      {/* ── Block 2: Approvals — mockup left, text right ── */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <ShowcaseBg flip />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Mockup (left on desktop) */}
            <div className="order-2 lg:order-1">
              <MockupWrapper>
                <ApprovalsMockup />
              </MockupWrapper>
            </div>

            {/* Text (right on desktop) */}
            <ScrollReveal direction="right" className="space-y-6 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-violet-200 dark:border-violet-500/30 bg-violet-50 dark:bg-violet-500/10 shadow-sm">
                <ShieldCheck className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
                <span className="text-sm font-medium text-violet-700 dark:text-violet-300">
                  Aprobaciones
                </span>
              </div>

              <div className="space-y-2">
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white leading-tight">
                  Nunca más un descuento
                  <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-cyan-600">
                    sin aprobación.
                  </span>
                </h2>
                <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                  Define rangos de descuento por rol. Dentro del límite se aprueban solos. Fuera del
                  rango, el aprobador recibe toda la información para decidir rápido.
                </p>
              </div>

              <ul className="space-y-3">
                {[
                  'Reglas por producto, categoría y tipo de cliente',
                  'Auto-aprobación para descuentos dentro del rango',
                  'Notificaciones instantáneas al aprobador',
                  'Historial completo de decisiones y ajustes',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="flex items-center gap-6 pt-2">
                {[
                  { icon: TrendingUp, value: '68%', label: 'auto-aprobadas' },
                  { icon: Clock, value: '4.2h', label: 'tiempo promedio' },
                  { icon: BarChart3, value: '2.1×', label: 'más velocidad' },
                ].map(({ icon: Icon, value, label }) => (
                  <div key={label} className="text-center">
                    <div className="flex items-center gap-1 justify-center mb-0.5">
                      <Icon className="w-3.5 h-3.5 text-violet-500 dark:text-violet-400" />
                      <span className="text-lg font-bold text-gray-900 dark:text-white">
                        {value}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500">{label}</p>
                  </div>
                ))}
              </div>

              <RegistroCta className="inline-flex items-center gap-2 text-sm font-semibold text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 transition-colors group">
                Configurar mi flujo de aprobaciones
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </RegistroCta>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </>
  );
}
